/**
 * One line telling a BigQuery-sourced report how far behind it is.
 *
 * `payment_succeeded` arrives late — 7 to 92 minutes on Science.co, measured
 * 2026-09-15 — so Traffic showed 7 payments from 13:00 until 16:30 while the
 * Daily Report showed 10, then agreed at 10 by 16:55. Both were right the whole
 * time. Without this strip the only reading available was that something had
 * broken.
 *
 * It says nothing when there is nothing to say: a report whose numbers already
 * match the money system renders no strip at all, so the absence of a warning
 * is itself information.
 */

import { useState, useEffect } from 'react'
import { format } from 'date-fns'
import { trafficAPI } from '../services/api'
import { Clock, AlertCircle } from 'lucide-react'

/** The freshest event BigQuery holds, in the reader's own clock. */
function freshnessNote(freshAsOf) {
  if (!freshAsOf) return null
  const at = new Date(freshAsOf)
  if (Number.isNaN(at.getTime())) return null
  return `data current to ${format(at, 'h:mm a')}`
}

export default function PaymentFreshnessStrip({ dateRange }) {
  const [state, setState] = useState(null)

  useEffect(() => {
    let cancelled = false
    if (!dateRange?.from || !dateRange?.to) return undefined
    trafficAPI
      .getPaymentReconciliation({
        start: format(dateRange.from, 'yyyy-MM-dd'),
        end: format(dateRange.to, 'yyyy-MM-dd'),
      })
      .then(res => { if (!cancelled) setState(res.data?.data ?? null) })
      // A banner about data quality must never become a data-quality problem.
      .catch(() => { if (!cancelled) setState(null) })
    return () => { cancelled = true }
  }, [dateRange])

  if (!state?.available || !state.pending) return null

  const { pending, waitingOnIngestion, missing, coreCharges, bigQueryPayments } = state
  const note = freshnessNote(state.freshAsOf)

  // Waiting and missing want different words: one resolves itself, the other
  // does not. When a window holds both, the stronger claim is the honest one.
  const allWaiting = missing === 0
  const Icon = allWaiting ? Clock : AlertCircle
  const tone = allWaiting
    ? 'border-border bg-muted/40 text-muted-foreground'
    : 'border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-300'

  const headline = allWaiting
    ? `${pending} ${pending === 1 ? 'payment has' : 'payments have'} not reached BigQuery yet`
    : `${pending} of ${coreCharges} payments are missing from BigQuery` +
      (waitingOnIngestion ? ` (${waitingOnIngestion} still arriving)` : '')

  return (
    <div className={`flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border px-3 py-2 text-xs ${tone}`}>
      <Icon className="h-3.5 w-3.5 flex-none" />
      <span className="font-medium">{headline}</span>
      <span className="opacity-70">
        · this report counts {bigQueryPayments} of the {coreCharges} charges the
        clinic has taken{note ? ` · ${note}` : ''}
      </span>
    </div>
  )
}
